import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { 
  X, CreditCard, Building2, Smartphone, Upload, Check, 
  Loader2, AlertCircle, Globe, MapPin, ChevronRight, Mail
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PayPalScriptProvider, PayPalButtons } from '@paypal/react-paypal-js';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const PAYPAL_CLIENT_ID = process.env.REACT_APP_PAYPAL_CLIENT_ID || 'AYXMwKbKbTQ84KxiAMGz-scb23b6PVpu-DPQjEz98JTdDvvIctq8dN_jjEIUGLM718Q_ANv9FizfFKsJ';

// Detect if user is from Pakistan (basic detection)
const detectPakistan = () => {
  const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const lang = navigator.language || navigator.userLanguage;
  return timezone?.includes('Karachi') || lang?.includes('pk') || lang?.includes('ur');
};

const PaymentModal = ({ 
  isOpen, 
  onClose, 
  packageId, 
  packageName, 
  packagePrice, 
  packageDescription,
  onSuccess,
  token,
  userEmail,
  userName
}) => {
  const [isPakistan, setIsPakistan] = useState(false);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [transactionId, setTransactionId] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadComplete, setUploadComplete] = useState(false);
  const fileInputRef = useRef(null);
  
  // Guest email collection for manual payments
  const [showEmailPrompt, setShowEmailPrompt] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [guestName, setGuestName] = useState('');
  const [pendingManualMethod, setPendingManualMethod] = useState(null);
  
  // PayPal states
  const [showPayPal, setShowPayPal] = useState(false);
  const [paypalProcessing, setPaypalProcessing] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setIsPakistan(detectPakistan());
      fetchPaymentInfo();
      // Reset state
      setSelectedMethod(null);
      setTransactionId(null);
      setUploadedFile(null);
      setUploadComplete(false);
      setShowEmailPrompt(false);
      setGuestEmail('');
      setGuestName('');
      setPendingManualMethod(null);
      setShowPayPal(false);
      setPaypalProcessing(false);
    }
  }, [isOpen]);

  const fetchPaymentInfo = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/payments/info`);
      setPaymentInfo(res.data);
    } catch (err) {
      console.error('Failed to fetch payment info:', err);
      toast.error('Failed to load payment options');
    } finally {
      setLoading(false);
    }
  };

  const handleStripePayment = async () => {
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/payments/stripe/checkout`, {
        package_id: packageId,
        origin_url: window.location.origin,
        user_email: userEmail,
        user_name: userName
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      // Redirect to Stripe checkout
      window.location.href = res.data.checkout_url;
    } catch (err) {
      console.error('Stripe checkout failed:', err);
      toast.error('Failed to initiate payment. Please try again.');
      setProcessing(false);
    }
  };

  const handlePayPalClick = () => {
    setShowPayPal(true);
  };

  const createPayPalOrder = (data, actions) => {
    return actions.order.create({
      purchase_units: [{
        description: packageName,
        amount: {
          currency_code: 'GBP',
          value: packagePrice.toString()
        }
      }]
    });
  };

  const onPayPalApprove = async (data, actions) => {
    setPaypalProcessing(true);
    try {
      const details = await actions.order.capture();
      
      // Record the PayPal payment in our backend
      await axios.post(`${API}/payments/paypal/capture`, {
        order_id: data.orderID,
        package_id: packageId,
        payer_email: details.payer?.email_address || userEmail,
        user_email: userEmail,
        user_name: userName,
        amount: packagePrice
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      toast.success('Payment successful! Welcome to Innovator Founder Visas.');
      setUploadComplete(true); // Show success state
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('PayPal capture failed:', err);
      toast.error('Payment processing failed. Please contact support.');
    } finally {
      setPaypalProcessing(false);
    }
  };

  const onPayPalError = (err) => {
    console.error('PayPal error:', err);
    toast.error('Payment failed. Please try again.');
    setShowPayPal(false);
  };

  const handleManualPayment = async (method) => {
    // If not logged in and no email entered, prompt for email
    const email = userEmail || guestEmail;
    const name = userName || guestName;
    
    if (!email) {
      setPendingManualMethod(method);
      setShowEmailPrompt(true);
      return;
    }
    
    setProcessing(true);
    try {
      const res = await axios.post(`${API}/payments/manual/initiate`, {
        package_id: packageId,
        payment_method: method,
        user_email: email,
        user_name: name || 'Guest User'
      }, {
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      });

      setTransactionId(res.data.transaction_id);
      setSelectedMethod(method);
      setShowEmailPrompt(false);
      toast.success('Payment initiated! Please complete the transfer and upload your receipt.');
    } catch (err) {
      console.error('Manual payment initiation failed:', err);
      toast.error('Failed to initiate payment');
    } finally {
      setProcessing(false);
    }
  };

  const handleEmailSubmit = () => {
    if (!guestEmail || !guestEmail.includes('@')) {
      toast.error('Please enter a valid email address');
      return;
    }
    if (pendingManualMethod) {
      handleManualPayment(pendingManualMethod);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validate file type
      const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf'];
      if (!validTypes.includes(file.type)) {
        toast.error('Please upload a JPG, PNG, or PDF file');
        return;
      }
      // Validate file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error('File size must be less than 5MB');
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleUploadReceipt = async () => {
    if (!uploadedFile || !transactionId) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('receipt', uploadedFile);

      await axios.post(`${API}/payments/manual/upload-receipt/${transactionId}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        }
      });

      setUploadComplete(true);
      toast.success('Receipt uploaded successfully!');
    } catch (err) {
      console.error('Receipt upload failed:', err);
      toast.error(err.response?.data?.detail || 'Failed to upload receipt');
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div 
        initial={{ opacity: 0 }} 
        animate={{ opacity: 1 }} 
        exit={{ opacity: 0 }} 
        className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
        onClick={onClose}
      >
        <motion.div 
          initial={{ scale: 0.95, opacity: 0 }} 
          animate={{ scale: 1, opacity: 1 }} 
          exit={{ scale: 0.95, opacity: 0 }} 
          className="bg-white rounded-2xl max-w-lg w-full shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-red-500 to-red-600 px-6 py-5 text-white">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold">Complete Your Purchase</h2>
                <p className="text-red-100 text-sm mt-1">{packageName}</p>
              </div>
              <button onClick={onClose} className="w-10 h-10 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition-colors">
                <X className="w-5 h-5" />
              </button>
            </div>
            <div className="mt-4 flex items-baseline gap-2">
              <span className="text-3xl font-bold">£{packagePrice}</span>
              <span className="text-red-200">one-time</span>
            </div>
          </div>

          {loading ? (
            <div className="p-8 flex items-center justify-center">
              <Loader2 className="w-8 h-8 animate-spin text-red-500" />
            </div>
          ) : uploadComplete ? (
            // Success State
            <div className="p-8 text-center">
              <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-10 h-10 text-green-600" />
              </div>
              <h3 className="text-xl font-bold text-slate-900 mb-2">Receipt Uploaded!</h3>
              <p className="text-slate-600 mb-6">
                Thank you for your payment. We will verify your receipt and activate your account within 24-48 hours.
              </p>
              <p className="text-sm text-slate-500 mb-6">
                You will receive a confirmation email at <strong>{userEmail || guestEmail}</strong> once verified.
              </p>
              <Button onClick={onClose} className="w-full bg-green-500 hover:bg-green-600">
                Close
              </Button>
            </div>
          ) : transactionId ? (
            // Upload Receipt State
            <div className="p-6">
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                <h3 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
                  {selectedMethod === 'easypaisa' ? (
                    <><Smartphone className="w-5 h-5" /> Easypaisa Payment Details</>
                  ) : (
                    <><Building2 className="w-5 h-5" /> Bank Transfer Details</>
                  )}
                </h3>
                
                {selectedMethod === 'easypaisa' && paymentInfo?.easypaisa && (
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Account Name:</span> <strong>{paymentInfo.easypaisa.account_name}</strong></p>
                    <p><span className="text-slate-500">Account Number:</span> <strong className="text-lg">{paymentInfo.easypaisa.account_number}</strong></p>
                    <p><span className="text-slate-500">Amount:</span> <strong className="text-green-600">PKR {(packagePrice * 280).toFixed(0)}</strong> <span className="text-xs text-slate-400">(approx.)</span></p>
                  </div>
                )}
                
                {selectedMethod === 'bank_transfer' && paymentInfo?.bank_transfer && (
                  <div className="space-y-2 text-sm">
                    <p><span className="text-slate-500">Bank:</span> <strong>{paymentInfo.bank_transfer.bank_name}</strong></p>
                    <p><span className="text-slate-500">Account Title:</span> <strong>{paymentInfo.bank_transfer.account_title}</strong></p>
                    <p><span className="text-slate-500">Account Number:</span> <strong>{paymentInfo.bank_transfer.account_number}</strong></p>
                    <p><span className="text-slate-500">IBAN:</span> <strong className="text-xs">{paymentInfo.bank_transfer.iban}</strong></p>
                    <p><span className="text-slate-500">Amount:</span> <strong className="text-green-600">PKR {(packagePrice * 280).toFixed(0)}</strong> <span className="text-xs text-slate-400">(approx.)</span></p>
                  </div>
                )}
              </div>

              {/* Upload Section */}
              <div className="border-2 border-dashed border-slate-300 rounded-xl p-6 text-center">
                <input 
                  type="file" 
                  ref={fileInputRef}
                  onChange={handleFileSelect}
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  className="hidden"
                />
                
                {uploadedFile ? (
                  <div>
                    <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Check className="w-8 h-8 text-green-600" />
                    </div>
                    <p className="font-medium text-slate-900">{uploadedFile.name}</p>
                    <p className="text-sm text-slate-500 mb-4">{(uploadedFile.size / 1024).toFixed(1)} KB</p>
                    <div className="flex gap-2 justify-center">
                      <Button variant="outline" onClick={() => { setUploadedFile(null); fileInputRef.current?.click(); }}>
                        Change File
                      </Button>
                      <Button onClick={handleUploadReceipt} disabled={uploading} className="bg-green-500 hover:bg-green-600">
                        {uploading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Upload className="w-4 h-4 mr-2" />}
                        Upload Receipt
                      </Button>
                    </div>
                  </div>
                ) : (
                  <>
                    <Upload className="w-12 h-12 text-slate-400 mx-auto mb-3" />
                    <p className="font-medium text-slate-900 mb-1">Upload Payment Receipt</p>
                    <p className="text-sm text-slate-500 mb-4">JPG, PNG or PDF (max 5MB)</p>
                    <Button onClick={() => fileInputRef.current?.click()} variant="outline">
                      Select File
                    </Button>
                  </>
                )}
              </div>

              <p className="text-xs text-slate-500 mt-4 text-center">
                After uploading, we will verify your payment and activate your account within 24-48 hours.
              </p>
            </div>
          ) : showEmailPrompt ? (
            // Email Prompt for Guest Users
            <div className="p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Mail className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Enter Your Details</h3>
                <p className="text-sm text-slate-500">We need your email to send payment confirmation</p>
              </div>
              
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Full Name</label>
                  <input 
                    type="text"
                    value={guestName}
                    onChange={(e) => setGuestName(e.target.value)}
                    placeholder="Enter your name"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email Address</label>
                  <input 
                    type="email"
                    value={guestEmail}
                    onChange={(e) => setGuestEmail(e.target.value)}
                    placeholder="your@email.com"
                    className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </div>
              </div>
              
              <div className="flex gap-3 mt-6">
                <Button variant="outline" onClick={() => setShowEmailPrompt(false)} className="flex-1">
                  Back
                </Button>
                <Button onClick={handleEmailSubmit} disabled={processing} className="flex-1 bg-blue-500 hover:bg-blue-600">
                  {processing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Continue
                </Button>
              </div>
            </div>
          ) : showPayPal ? (
            // PayPal Checkout
            <div className="p-6">
              <button 
                onClick={() => setShowPayPal(false)} 
                className="flex items-center gap-1 text-slate-500 hover:text-slate-700 mb-4 text-sm"
              >
                ← Back to payment methods
              </button>
              
              <div className="mb-4 p-4 bg-blue-50 rounded-xl">
                <p className="text-sm text-blue-800">
                  <strong>Package:</strong> {packageName}<br />
                  <strong>Amount:</strong> £{packagePrice}
                </p>
              </div>
              
              {paypalProcessing ? (
                <div className="flex flex-col items-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-blue-500 mb-4" />
                  <p className="text-slate-600">Processing your payment...</p>
                </div>
              ) : (
                <PayPalScriptProvider options={{ 
                  clientId: PAYPAL_CLIENT_ID, 
                  currency: 'GBP',
                  intent: 'capture'
                }}>
                  <PayPalButtons
                    style={{ 
                      layout: 'vertical',
                      color: 'blue',
                      shape: 'rect',
                      label: 'pay'
                    }}
                    createOrder={createPayPalOrder}
                    onApprove={onPayPalApprove}
                    onError={onPayPalError}
                    onCancel={() => {
                      toast.info('Payment cancelled');
                      setShowPayPal(false);
                    }}
                  />
                </PayPalScriptProvider>
              )}
              
              <p className="text-xs text-slate-500 text-center mt-4">
                Secure payment powered by PayPal
              </p>
            </div>
          ) : (
            // Payment Method Selection
            <div className="p-6">
              {/* Location Toggle */}
              <div className="flex items-center justify-center gap-4 mb-6 p-3 bg-slate-100 rounded-xl">
                <button 
                  onClick={() => setIsPakistan(false)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${!isPakistan ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
                >
                  <Globe className="w-4 h-4" /> International
                </button>
                <button 
                  onClick={() => setIsPakistan(true)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${isPakistan ? 'bg-white shadow text-slate-900' : 'text-slate-500'}`}
                >
                  <MapPin className="w-4 h-4" /> Pakistan
                </button>
              </div>

              <div className="space-y-3">
                {/* Stripe - Credit/Debit Card */}
                <button
                  onClick={handleStripePayment}
                  disabled={processing}
                  className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-blue-300 hover:bg-blue-50 transition-colors text-left flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center group-hover:bg-blue-200 transition-colors">
                    <CreditCard className="w-6 h-6 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">Credit / Debit Card</p>
                    <p className="text-sm text-slate-500">Pay securely with Stripe</p>
                  </div>
                  {processing ? (
                    <Loader2 className="w-5 h-5 animate-spin text-blue-500" />
                  ) : (
                    <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-blue-500 transition-colors" />
                  )}
                </button>

                {/* PayPal - Always shown for international */}
                <button
                  onClick={handlePayPalClick}
                  disabled={processing}
                  className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-yellow-300 hover:bg-yellow-50 transition-colors text-left flex items-center gap-4 group"
                >
                  <div className="w-12 h-12 bg-yellow-100 rounded-xl flex items-center justify-center group-hover:bg-yellow-200 transition-colors">
                    <svg className="w-6 h-6" viewBox="0 0 24 24" fill="none">
                      <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.77.77 0 0 1 .76-.654h6.96c2.3 0 4.063.584 5.09 1.686.934 1.005 1.28 2.404 1.03 4.153-.32 2.262-1.14 3.973-2.44 5.09-1.29 1.104-3.05 1.666-5.23 1.666H8.766a.771.771 0 0 0-.76.654l-.93 5.022z" fill="#003087"/>
                      <path d="M18.973 7.871c-.33 2.334-1.957 2.334-3.537 2.334h-.897l.631-3.998a.39.39 0 0 1 .385-.328h.412c1.075 0 2.09 0 2.614.612.314.366.41.91.392 1.38z" fill="#002F86"/>
                      <path d="M19.737 7.576c-.007.047-.014.096-.022.146-.656 3.374-2.899 4.54-5.764 4.54H12.54a.709.709 0 0 0-.7.6l-.88 5.579-.25 1.582a.373.373 0 0 0 .368.432h2.586a.623.623 0 0 0 .615-.526l.025-.132.488-3.09.031-.17a.623.623 0 0 1 .616-.527h.388c2.512 0 4.478-1.02 5.053-3.972.24-1.233.116-2.262-.52-2.985a2.476 2.476 0 0 0-.623-.477z" fill="#009CDE"/>
                    </svg>
                  </div>
                  <div className="flex-1">
                    <p className="font-semibold text-slate-900">PayPal</p>
                    <p className="text-sm text-slate-500">Pay with your PayPal account</p>
                  </div>
                  <ChevronRight className="w-5 h-5 text-slate-400 group-hover:text-yellow-600 transition-colors" />
                </button>

                {/* Pakistan-specific options */}
                {isPakistan && (
                  <>
                    <button
                      onClick={() => handleManualPayment('easypaisa')}
                      disabled={processing}
                      className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-green-300 hover:bg-green-50 transition-colors text-left flex items-center gap-4 group"
                    >
                      <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center group-hover:bg-green-200 transition-colors">
                        <Smartphone className="w-6 h-6 text-green-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">Easypaisa</p>
                        <p className="text-sm text-slate-500">Mobile wallet transfer</p>
                      </div>
                      <Badge className="bg-green-100 text-green-700">Pakistan</Badge>
                    </button>

                    <button
                      onClick={() => handleManualPayment('bank_transfer')}
                      disabled={processing}
                      className="w-full p-4 border-2 border-slate-200 rounded-xl hover:border-purple-300 hover:bg-purple-50 transition-colors text-left flex items-center gap-4 group"
                    >
                      <div className="w-12 h-12 bg-purple-100 rounded-xl flex items-center justify-center group-hover:bg-purple-200 transition-colors">
                        <Building2 className="w-6 h-6 text-purple-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-semibold text-slate-900">Bank Transfer</p>
                        <p className="text-sm text-slate-500">Direct bank transfer</p>
                      </div>
                      <Badge className="bg-purple-100 text-purple-700">Pakistan</Badge>
                    </button>
                  </>
                )}
              </div>

              {/* Security Note */}
              <div className="mt-6 p-3 bg-slate-50 rounded-lg flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-slate-500">
                  Your payment information is secure. We use industry-standard encryption and never store your card details.
                </p>
              </div>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PaymentModal;
